import React, {useCallback} from "react";
import {Card, H2, Text, Button, theme, styled, Icon, FlexRow} from "@reside/ui";
import {saveAs} from "file-saver";
import {useFacilityCodesQuery} from "./hooks/useFacilityCodesQuery";

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.space[5]}px;
`;

const FACILITY_CODES_CSV_FILENAME = "facility_codes.csv";

export const FacilityCodesCard = (): JSX.Element => {
  const {data, isLoading, isError, refetch} = useFacilityCodesQuery();

  const downloadCsv = useCallback(() => {
    if (!data?.facilityCodes) {
      return;
    }

    const headers = ["Facility Name", "Facility Code"];
    const rows = data.facilityCodes.map(fc => [
      `"${fc.name.replace(/"/g, '""')}"`,
      `"${fc.code.replace(/"/g, '""')}"`,
    ]);

    const csvContent = [headers.join(","), ...rows.map(e => e.join(","))].join(
      "\n",
    );

    const blob = new Blob([csvContent], {type: "text/csv;charset=utf-8;"});
    saveAs(blob, FACILITY_CODES_CSV_FILENAME);
  }, [data]);

  return (
    <Card style={{padding: theme.space[7]}}>
      <CardHeader>
        <H2>Facility Codes</H2>
        <FlexRow>
          <Button
            outline
            color="primary"
            onClick={downloadCsv}
            disabled={isLoading || isError}
          >
            Download Facility Codes CSV
          </Button>
          &nbsp;
          {isError && (
            <Button
              outline
              color="danger"
              onClick={() => refetch()}
              style={{padding: `${theme.space[1]}px ${theme.space[2]}px`}}
            >
              <Icon name="arrows-cw" size={14} />
              &nbsp; Retry loading facility codes
            </Button>
          )}
        </FlexRow>
      </CardHeader>
      <Text as="p" fontSize={[3]} style={{marginBottom: "32px"}}>
        Download a list of facility names and facility codes for this
        organization.
      </Text>
    </Card>
  );
};
